import React from 'react';
import { FileText, Images, ExternalLink } from 'lucide-react';

/**
 * Rich attachment card rendered inside a conversation message —
 * either a board Document or a set of Gallery images. Used on both
 * the owner (Board Editor) and client (Client View) sides.
 */

const resolveThumb = (img) => img?.image_thumb || img?.image_data || img?.external_url || '';

const scrollToImages = (imageIds) => {
  const first = (imageIds || [])[0];
  let target = null;
  if (first) {
    target = document.querySelector(`[data-testid="client-image-clickable-${first}"]`)
      || document.querySelector(`[data-image-id="${first}"]`);
    if (target) target = target.closest('.image-card') || target;
  }
  if (!target) {
    target = document.getElementById('client-section-gallery')
      || document.querySelector('.moodboard-grid')
      || document.querySelector('.image-grid');
  }
  if (!target) return;
  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  (imageIds || []).forEach((id) => {
    let el = document.querySelector(`[data-testid="client-image-clickable-${id}"]`)
      || document.querySelector(`[data-image-id="${id}"]`);
    el = el && (el.closest('.image-card') || el);
    if (el) {
      el.classList.add('cv-attach-highlight');
      setTimeout(() => el.classList.remove('cv-attach-highlight'), 2800);
    }
  });
};

const cardBase = {
  display: 'flex', alignItems: 'center', gap: '12px',
  marginTop: '10px', padding: '10px 14px',
  background: 'var(--kahla-white)', border: '1px solid var(--kahla-border)',
  cursor: 'pointer', width: '100%', textAlign: 'left',
  fontFamily: 'inherit', transition: 'border-color 200ms ease, background-color 200ms ease',
};

const NoteAttachmentCard = ({ attachment, documents = [], images = [], t = {} }) => {
  if (!attachment) return null;

  if (attachment.type === 'document') {
    const doc = documents.find((d) => d.id === attachment.document_id);
    const label = doc?.title || doc?.filename || attachment.filename || 'Document';
    const meta = doc
      ? (doc.type === 'link' ? (t.doc_link || 'Link') : `${(label.split('.').pop() || '').toUpperCase().slice(0, 5)}${doc.file_size_display ? ` · ${doc.file_size_display}` : ''}`)
      : (t.attachment_removed || 'Document removed');
    const open = () => {
      if (!doc) return;
      const url = doc.type === 'link' ? doc.url : doc.file_url;
      if (url) window.open(url, '_blank', 'noopener');
    };
    return (
      <button
        type="button"
        onClick={open}
        data-testid="note-attachment-document"
        style={{ ...cardBase, opacity: doc ? 1 : 0.55, cursor: doc ? 'pointer' : 'default' }}
        onMouseEnter={(e) => { if (doc) e.currentTarget.style.borderColor = 'var(--kahla-taupe)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--kahla-border)'; }}
      >
        <span style={{
          width: '34px', height: '34px', flexShrink: 0,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: 'var(--kahla-cream-light)', border: '1px solid var(--kahla-border)',
          color: 'var(--kahla-taupe)',
        }}>
          {doc?.type === 'link' ? <ExternalLink size={15} /> : <FileText size={15} />}
        </span>
        <span style={{ minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: '13px', color: 'var(--kahla-charcoal)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {label}
          </span>
          <span style={{ display: 'block', fontSize: '11px', color: 'var(--kahla-text-light)', marginTop: '2px' }}>
            {meta}
          </span>
        </span>
      </button>
    );
  }

  if (attachment.type === 'gallery') {
    const ids = attachment.image_ids || [];
    const thumbs = ids
      .map((id) => images.find((im) => im.id === id))
      .filter(Boolean)
      .slice(0, 3);
    const extra = ids.length - thumbs.length > 0 && ids.length > 3 ? ids.length - 3 : 0;
    return (
      <button
        type="button"
        onClick={() => scrollToImages(ids)}
        data-testid="note-attachment-gallery"
        style={{ ...cardBase, flexDirection: 'column', alignItems: 'stretch', gap: '10px' }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--kahla-taupe)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--kahla-border)'; }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '12px', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--kahla-taupe)' }}>
          <Images size={14} /> {t.view_board_card || 'View board'}
        </span>
        {thumbs.length > 0 && (
          <span style={{ display: 'flex', gap: '8px' }}>
            {thumbs.map((im) => (
              <img
                key={im.id}
                src={resolveThumb(im)}
                alt=""
                style={{ width: '72px', height: '72px', objectFit: 'cover', display: 'block', background: 'var(--kahla-cream-light)' }}
              />
            ))}
            {extra > 0 && (
              <span style={{
                width: '72px', height: '72px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: 'var(--kahla-cream-light)', border: '1px solid var(--kahla-border)',
                fontSize: '13px', color: 'var(--kahla-text-light)',
              }}>+{extra}</span>
            )}
          </span>
        )}
      </button>
    );
  }

  return null;
};

export default NoteAttachmentCard;
